<template>
  <div v-if="taggingItem" class="message-details-wrapper">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <div class="message-details-header">
      <h2 class="message-details-title">
        Details
      </h2>
      <WhiteCloseIcon28 @click="closeDetails" />
    </div>
    <div class="menu-item-wrapper">
      <div class="menu-item-header">
        <h2 class="menu-item-title">
          Message
        </h2>
        <GreyArrowUpIcon24
          :style="{ transform: isViewMessage ? '' : 'rotate(180deg)' }"
          @click="isViewMessage = !isViewMessage"
        />
      </div>
      <transition v-if="isViewMessage" name="slide">
        <div class="item-wrapper">
          <GalleryItem
            v-if="galleryId"
            :id="'media_attachment_tagging_item' + galleryId"
            class="attachment-media"
            :attachment="taggingItem"
            :in-panel-view="true"
            @updateMedia="updateTaggingItem"
            @openMediaPopup="openMediaPopup"
          />
        </div>
      </transition>
    </div>
    <div v-if="hasPermission(['add_tag'])" class="menu-item-wrapper">
      <div class="menu-item-header">
        <div class="header-text">
          <h2 class="menu-item-title">
            Tags
          </h2>
          <span @click="$refs.editTagsPopup.openPopups()">Edit</span>
        </div>
        <GreyArrowUpIcon24
          :style="{ transform: isViewTags ? '' : 'rotate(180deg)' }"
          @click="isViewTags = !isViewTags"
        />
      </div>
      <transition v-if="isViewTags" name="slide">
        <div class="tags-wrapper" :class="{ 'tags-disabled': isLoading }">
          <div v-for="(category, index) in galleryTagsList" :key="index" class="tag-section">
            <h2 class="section-header">
              {{ category.category.name }}
            </h2>
            <div class="section-tags">
              <div v-for="(tag, i) in category.tags" :key="i" class="tag">
                <div v-if="tag.tag" class="check">
                  <CustomCheckbox
                    :id="'tag_' + tag.id"
                    :input-element="tag"
                    custom-class="no-pos"
                    @checked="changeTagState(tag)"
                  />
                </div>
                <span>{{ tag.tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="menu-item-wrapper">
      <div class="menu-item-header">
        <h2 class="menu-item-title">
          Comments
        </h2>
        <GreyArrowUpIcon24
          :style="{ transform: isViewComments ? '' : 'rotate(180deg)' }"
          @click="isViewComments = !isViewComments"
        />
      </div>
      <transition v-if="isViewComments" name="slide">
        <div class="comments-wrapper">
          <div class="comments-item-wrapper">
            <div v-for="(comment, index) in comments" :key="index" class="comment-item">
              <CommentMessage
                :comment="comment"
                @deleted="openDeleteCommentSuccessPopup"
                @changeComment="handleChangeComment"
                @deleteComment="handleDeleteComment"
              />
            </div>
          </div>
          <textarea
            v-if="hasPermission(['add_comments_highlights'])"
            v-model="newCommentMessage"
            name="invite"
            class="comment-textarea"
            placeholder="Add comment"
            @keydown.enter.exact.prevent="addComment"
          />
          <button
            v-if="hasPermission(['add_comments_highlights'])"
            id="button_create_case"
            class="btn btn-revert"
            @click="addComment"
          >
            Send
          </button>
        </div>
      </transition>
      <EditTagsPopup
        ref="editTagsPopup"
        :tags-list="tagsList"
        @addTag="addTag"
        @deleteTag="deleteTag"
        @changeTag="changeTagName"
        @addNewCategory="addCategory"
        @deleteCategory="deleteCategory"
        @changeTagOrder="changeTagsOrder"
        @changeCategory="changeCategoryName"
      />
    </div>
    <CommonDialog ref="commentDeleteDialogMD">
      <BlueSuccessIcon94 />
      <h2
        class="dialog-message-title"
      >
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The comment was deleted
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteTagDialog">
      <BlueSuccessIcon94 />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The tag was deleted.
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteCategoryDialog">
      <BlueSuccessIcon94 />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        The category was deleted.
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteCategoryWarningDialog">
      <h2 class="dialog-message-title">
        Deleting category
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Before you can delete a category, you need to remove
        all tags from it
      </p>
    </CommonDialog>
    <CommonDialog ref="commonDeleteTagWarningDialog">
      <h2 class="dialog-message-title">
        Deleting tag
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Before you can delete this tag, you need to remove it
        from all messages in this chat where it is used
      </p>
    </CommonDialog>
  </div>
</template>

<script>
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import parseDate from '@/mixins/parseDate';
import parseDateTime from '@/mixins/parseTime';
import {mapGetters} from 'vuex';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import redactIcon from '@/assets/img/case-messages/redacted.png';
import priorityIcon from '@/assets/img/case-messages/priority.png';
import deleteIcon from '@/assets/img/case-messages/delete.svg';
import unredactIcon from '@/assets/img/case-messages/unredact.svg';
import EditTagsPopup from '@/components/popups/EditTagsPopup.vue';
import TaggingComment from '@/components/cases/case-messages/tagging-common/TaggingComment.vue';
import CommonDialog from '@/components/popups/CommonDialog.vue';
import hasPermission from '@/utils/permission';
import GalleryItem from '@/components/cases/case-messages/media/GalleryItem.vue';
import mediaTagging from '@/mixins/mediaTagging';
import WhiteCloseIcon28 from '@/components/icons/WhiteCloseIcon28.vue';
import GreyArrowUpIcon24 from '@/components/icons/GreyArrowUpIcon24.vue';
import BlueSuccessIcon94 from '@/components/icons/BlueSuccessIcon94.vue';

export default {
  name: 'MediaTaggingPanel',
  components: {
    BlueSuccessIcon94,
    GreyArrowUpIcon24,
    WhiteCloseIcon28,
    GalleryItem,
    CommonDialog,
    CommentMessage: TaggingComment,
    EditTagsPopup,
    AlertErrorMsg,
    CustomCheckbox,
  },
  emits: ['closeDetails', 'changeCheckbox', 'updateMainItem', 'updateComments', 'openMediaPopup'],
  mixins: [parseDate, parseDateTime, mediaTagging],
  props: {
    updateMedia: {
      type: Number,
      default: null,
    }
  },
  data() {
    return {
      unredactIcon,
      redactedImg: redactIcon,
      priorityImg: priorityIcon,
      deleteIcon,
      newComment: '',
      errorMessage: '',
      newCommentMessage: '',
      infoDialogData: {
        dialogTitle: '',
        dialogText: '',
        confirmBtnText: '',
        cancelBtnText: '',
      },
      commentEdit: false,
      isViewMessage: true,
      isViewTags: true,
      isViewComments: true,
      editingCommentId: null,
      galleryId: null,
    };
  },
  computed: {
    ...mapGetters('mediaTagging', {
      taggingItem: 'getTaggingItem',
      isLoading: 'isLoading',
    }),
  },
  watch: {
    taggingItem: {
      handler(newVal) {
        if (newVal) {
          this.galleryId = newVal.id;
          this.getListComments();
          this.getListTagsForGallery();
        }
      },
      deep: true,
      immediate: true
    },
    async updateMedia(newVal) {
      if (newVal && newVal !== this.taggingItem.id) {
        await this.updateTaggingItem(newVal);
      }
    },
  },
  async mounted() {
    await this.getListTags();
    await this.getListTagsForGallery();
  },
  methods: {
    hasPermission,

    updateTaggingItem() {
      this.getMediaByGalleryId();
    },

    async addComment() {
      if (this.newCommentMessage) {
        await this.createComment(this.taggingItem.id, {text: this.newCommentMessage});
        this.newCommentMessage = '';
        await this.getListComments();
        this.$emit('updateComments');
      }
    },
    openDeleteCommentSuccessPopup() {
      this.$refs.commentDeleteDialogMD.openPopups();
    },

    async changeTagState(tag) {
      if (tag.isActive) {
        await this.addTagToGallery(tag.id);
      } else {
        await this.removeTagFromGallery(tag.id);
      }
      this.$emit('updateMainItem');
    },

    closeDetails() {
      this.$router.push({name: 'Case messages media list'});
    },

    async handleChangeComment(commentId, newText) {
      await this.editComment(commentId, {text: newText});
      await this.getListComments();
      this.$emit('updateComments');
    },

    async handleDeleteComment(commentId) {
      await this.deleteComment(commentId);
      this.openDeleteCommentSuccessPopup();
      await this.getListComments();
      this.$emit('updateComments');
    },

    // Tag and Category Management
    async addTag(tagInfo) {
      await this.addNewTag(tagInfo.categoryId, {tag: tagInfo.newTagName});
    },

    async changeTagName(tagInfo) {
      await this.editTag(tagInfo.tagId, {tag: tagInfo.tagName});
    },

    async changeTagsOrder(orderData) {
      await this.changeTagOrder(orderData);
    },

    async deleteTag(tagId) {
      await this.$load(async () => {
        await this.removeTag(tagId);
        this.$refs.commonDeleteTagDialog.openPopups();
      }, () => {
        this.$refs.commonDeleteTagWarningDialog.openPopups();
      });
    },

    async addCategory(categoryName) {
      await this.addNewCategory({category: categoryName});
    },

    async changeCategoryName(categoryInfo) {
      await this.editCategory(categoryInfo.categoryId, {category: categoryInfo.categoryName});
    },

    async deleteCategory(categoryId) {
      await this.removeCategory(categoryId);
    },

    openMediaPopup(itemId) {
      this.$emit('openMediaPopup', itemId);
    },
  },
};
</script>
<style scoped lang="scss">
.message-details-wrapper {
  max-width: 435px;
  max-height: 1031px;
  width: 100%;
  height: 85vh;
  border-left: 1px solid var(--border-color);
  scrollbar-color: var(--background-color) transparent;
  scrollbar-width: thin;

  .message-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 25px;

    .message-details-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      line-height: 21.78px;
      color: var(--secondary-black-color);
    }
  }

  .menu-item-wrapper {

    .menu-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 25px;
      background-color: var(--hover-accent-color);

      .header-text {
        display: flex;
        align-items: center;
        gap: 10px;

        span {
          font-size: 14px;
          font-weight: 400;
          line-height: 16.94px;
          text-align: left;
          color: var(--light-gray);
          cursor: pointer;
        }
      }

      .menu-item-title {
        font-size: 14px;
        font-weight: 700;
        line-height: 21px;
        text-align: left;
        text-transform: uppercase;
        color: var(--secondary-black-color);
      }

      svg {
        transition: transform 0.3s;
      }
    }
  }
}

.item-wrapper {
  min-height: 310px;
  max-width: 430px;
  width: 100%;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tags-wrapper {
  padding: 0 25px 15px;
  transition: all .5s;
  height: 190px;
  overflow-y: scroll;

  .tag-section {
    padding-top: 15px;

    .section-header {
      font-size: 16px;
      font-weight: 700;
      line-height: 19.36px;
      text-align: left;
      color: var(--accent-color);
      padding-bottom: 15px;
    }

    .section-tags {

      .tag {
        display: flex;
        gap: 10px;
      }
    }

    &:not(:last-child) {
      padding-bottom: 15px;
      border-bottom: 1px solid var(--border-color);
    }
  }
}

.tags-disabled {
  opacity: 0.1;
  pointer-events: none;
}

.comments-wrapper {
  padding: 0 0 25px 25px;

  .comments-item-wrapper {
    height: fit-content;
    max-height: 190px;
    overflow-y: scroll;
  }

  .comment-textarea {
    width: 100%;
    resize: none;
    min-height: 104px;
    outline: none;
    border: 1px solid var(--input-placeholder-value-color);
    border-radius: 16px !important;
    padding: 16px 24px;
    font-size: 14px;
    font-weight: 500;
    background: transparent;
    z-index: 2;
    color: var(--secondary-text-color);
    margin: 15px 0;
  }

  .btn {
    max-width: 120px;
    height: 35px;
  }
}

h2 {
  margin: 0;
}

svg {
  cursor: pointer;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.slide-enter-to, .slide-leave {
  max-height: 1000px;
  opacity: 1;
}

.tools {
  position: absolute;
  z-index: 10;
  transition: 0.2s all;
  border: 1px solid var(--case-btn-border-color);
  border-radius: 8px;
  padding: 8px;
  background-color: var(--white-color);
  font-size: 12px;

  max-width: fit-content;
  min-width: 250px;
  width: 100%;
  height: fit-content;
  box-shadow: 0 4px 8px 0 #00000029;
}

.colors {
  display: flex;
  align-items: center;
  gap: 16px;

  .close {
    border: none;
    width: fit-content;
    padding: 0;
    background-color: transparent;
  }

  .color {
    border: 1px solid transparent;
    border-radius: 50%;
    width: 16px;
    height: 16px;

    &.white {
      border-color: var(--case-btn-border-color);
    }

    &:hover,
    &.active {
      border-color: #373c44;
    }
  }
}

.underline {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.background {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.blurred {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: var(--black-color);
  pointer-events: none;
  width: 100%;
}

.edit-message-textarea {
  display: block;
  border: .5px solid var(--light-gray);
  border-radius: 0 !important;
  height: auto;
  padding: 0 !important;
  width: 100%;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 0;
  display: block;
  width: 100%;
  margin-top: 25px;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  width: 100%;
  margin-top: 20px;
}

.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 0;
  display: block;
  width: 100%;
  margin-top: 25px;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  text-align: center;
  color: #696F79;
  width: 100%;
  margin-top: 20px;
}

.no-pos {
  position: unset;
}

</style>
